import dayjs from 'dayjs';
import type { ComponentType } from 'react';
import {
  AlertCircle,
  Bell01,
  CheckCircle,
  Clock,
  MinusCircle,
  Pencil01,
  Send01,
  ShieldTick,
  UserQuestionmark,
  XCircle,
} from '@kula-ai/icons-core';
import consentErrors from '@/utils/errors/Modules/consent';
import type { ConsentLifecycleAction } from './api';
import type {
  CandidateConsentSummary,
  ConsentActionKey,
  DerivedConsentStatus,
} from './types';

export const EXPIRING_SOON_DAYS = 30;

// BE caps reminder nudges at CandidateConsent::MAX_REMINDERS.
export const MAX_REMINDERS = 3;

// Request/remind gate on the BE-computed booleans (CORE-4890) — never re-derive
// from status. Undefined (pre-rollout) → not disabled, so the button stays live
// and the BE toasts the mapped reason on click.
export const isRequestDisabled = (
  consent: Pick<CandidateConsentSummary, 'requestable'> | null | undefined
): boolean => consent?.requestable === false;

// Prefer `remindable`; fall back to the raw count only when the boolean is
// absent (pre-rollout).
export const isRemindDisabled = (
  consent:
    | Pick<CandidateConsentSummary, 'remindable' | 'reminder_count'>
    | null
    | undefined
): boolean => {
  if (consent?.remindable !== undefined) return !consent.remindable;
  return (consent?.reminder_count ?? 0) >= MAX_REMINDERS;
};

// Expiry is time-derived on the current BE stack: a lapsed consent may still
// read `granted` with a past expires_at, so always compute it here.
export const deriveConsentStatus = (
  consent: Pick<CandidateConsentSummary, 'status' | 'expires_at'>
): DerivedConsentStatus => {
  const { status, expires_at } = consent;
  if (status === 'expired') return 'expired';
  if (status === 'granted' && expires_at) {
    const expiry = new Date(expires_at).getTime();
    if (expiry < Date.now()) return 'expired';
    const soonCutoff = Date.now() + EXPIRING_SOON_DAYS * 24 * 60 * 60 * 1000;
    if (expiry < soonCutoff) return 'expiring_soon';
  }
  return status;
};

type IconComponent = ComponentType<{ size?: number; className?: string }>;

export interface ConsentTagConfig {
  label: string;
  variant: 'neutral' | 'warning';
  // Pastel overrides win over the variant surface (merged after presets)
  className?: string;
  Icon: IconComponent;
}

export const CONSENT_STATUS_TAG: Record<
  DerivedConsentStatus,
  ConsentTagConfig
> = {
  not_collected: {
    label: 'Consent not collected yet',
    variant: 'warning',
    className: 'bg-pastel-10-light text-pastel-10-dark',
    Icon: MinusCircle,
  },
  requested: {
    label: 'Consent request sent',
    variant: 'neutral',
    className: 'bg-secondary-50 text-secondary-700',
    Icon: Send01,
  },
  granted: {
    label: 'Consent granted',
    variant: 'neutral',
    className: 'bg-pastel-2-light text-pastel-2-dark',
    Icon: CheckCircle,
  },
  // Still technically granted, but nearing expiry — its own amber tag so the
  // recruiter can tell it apart from a healthy consent at a glance.
  expiring_soon: {
    label: 'Consent about to expire',
    variant: 'warning',
    className: 'bg-pastel-10-light text-pastel-10-dark',
    Icon: Clock,
  },
  expired: {
    label: 'Consent expired',
    variant: 'neutral',
    className: 'bg-pastel-1-light text-pastel-1-dark',
    Icon: AlertCircle,
  },
  withdrawn: {
    label: 'Consent withdrawn',
    variant: 'neutral',
    className: 'bg-pastel-1-light text-pastel-1-dark',
    Icon: XCircle,
  },
  refused: {
    label: 'Consent refused',
    variant: 'neutral',
    className: 'bg-pastel-1-light text-pastel-1-dark',
    Icon: XCircle,
  },
  // Product: blocked has no badge of its own — it is the 3rd refusal, so it
  // keeps showing Refused; request/remind are simply unavailable.
  blocked: {
    label: 'Consent refused',
    variant: 'neutral',
    className: 'bg-pastel-1-light text-pastel-1-dark',
    Icon: XCircle,
  },
  // Product: not_required displays as plain "Consented"
  not_required: {
    label: 'Consent granted',
    variant: 'neutral',
    className: 'bg-pastel-2-light text-pastel-2-dark',
    Icon: CheckCircle,
  },
  no_expiry: {
    label: 'Consent granted',
    variant: 'neutral',
    className: 'bg-pastel-2-light text-pastel-2-dark',
    Icon: ShieldTick,
  },
};

// Overrides the status tag when the candidate is anonymized (terminal state —
// no consent actions are available).
export const ANONYMIZED_TAG: ConsentTagConfig = {
  label: 'Anonymized',
  variant: 'neutral',
  className: 'bg-secondary-50 text-secondary-700',
  Icon: UserQuestionmark,
};

export interface ConsentActionConfig {
  label: string;
  Icon: IconComponent;
}

export const CONSENT_ACTION_CONFIG: Record<
  ConsentActionKey,
  ConsentActionConfig
> = {
  request: { label: 'Request consent', Icon: Send01 },
  remind: { label: 'Send reminder', Icon: Bell01 },
  // Records consent collected off-platform. On an active consent this reads "Extend consent
  // manually" (see EXTENDABLE_STATUSES); both open the manual-validity modal.
  mark_manually: { label: 'Mark consent manually', Icon: Pencil01 },
  withdraw: { label: 'Mark as consent withdrawn', Icon: XCircle },
  anonymize: { label: 'Anonymize candidate', Icon: UserQuestionmark },
};

const DAY_MS = 24 * 60 * 60 * 1000;

// Whole days either side of now, always counted in the positive direction so the floor rounds
// the way a reader expects: 25 hours out is "1 day", 23 hours out is "today" — and likewise for
// an expiry 25 hours *ago*. (Flooring a negative diff would round away from zero and report an
// hour-old expiry as a day old.)
const daysUntil = (iso: string): number =>
  Math.floor((new Date(iso).getTime() - Date.now()) / DAY_MS);

const daysSince = (iso: string): number =>
  Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS);

const pluralDays = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

export interface ConsentStatusNote {
  title: string;
  body: string;
}

// Heads the tag menu for the two statuses where *when* matters as much as *what*. Everything else
// gets no note — the chip already says all there is to say.
export const consentStatusNote = (
  status: DerivedConsentStatus,
  consent: Pick<CandidateConsentSummary, 'expires_at'> | null | undefined
): ConsentStatusNote | null => {
  const expiresAt = consent?.expires_at;
  if (!expiresAt) return null;

  if (status === 'expiring_soon') {
    const days = daysUntil(expiresAt);
    return {
      title: days <= 0 ? 'Expiring today' : `Expiring in ${pluralDays(days)}`,
      body: 'Request an extension to keep working with this candidate.',
    };
  }

  if (status === 'expired') {
    const days = daysSince(expiresAt);
    return {
      title: days <= 0 ? 'Expired today' : `Expired ${pluralDays(days)} ago`,
      body: 'Request consent again, or anonymize the candidate.',
    };
  }

  return null;
};

// Toast copy for the one-click lifecycle actions. Keyed by endpoint, so a new lifecycle action
// without copy is a compile error rather than a toast titled `undefined`.
export const CONSENT_ACTION_SUCCESS: Record<ConsentLifecycleAction, string> = {
  request: 'Consent request sent successfully',
  remind: 'Reminder sent successfully',
  mark_granted: 'Consent marked as granted successfully',
  mark_not_required: 'Consent marked as not required',
  withdraw: 'Consent withdrawn successfully',
};

// Every consent action but anonymize needs somewhere to send the request, so a candidate with no
// email address gets no consent menu at all — anonymize stays reachable from the row/more menu.
export const NO_EMAIL_TOOLTIP =
  'This candidate has no email address. Add one to request consent.';

// Statuses whose `request` action reads "Request consent extension" (vs plain "Request consent"):
// a live consent asking to extend early, or a lapsed one asking to renew — both send the extension
// template. Withdrawn is deliberately excluded — product wants plain "Request consent" there.
export const RENEWABLE_STATUSES: DerivedConsentStatus[] = [
  'granted',
  'expiring_soon',
  'expired',
];

export const REQUEST_EXTENSION_LABEL = 'Request consent extension';

// States that surface as the full status banner (needs recruiter attention);
// granted / no_expiry / not_required stay the compact chip. Anonymized is
// terminal and handled separately.
export const ATTENTION_STATUSES: DerivedConsentStatus[] = [
  'not_collected',
  'requested',
  'expiring_soon',
  'expired',
  'withdrawn',
  'refused',
  'blocked',
];

export const consentNeedsBanner = (
  consent: CandidateConsentSummary | null | undefined
): boolean =>
  !consent?.anonymized &&
  ATTENTION_STATUSES.includes(
    deriveConsentStatus(
      consent ?? { status: 'not_collected', expires_at: null }
    )
  );

// Parenthesised sub-line under the banner label, from the dates we already
// have. The "will be sent on {date}" scheduled variants need a BE field the
// summary doesn't carry yet, so they stay null for now.
export const consentBannerNote = (
  derived: DerivedConsentStatus,
  consent: CandidateConsentSummary | null | undefined,
  dateFormatLabel?: string
): string | null => {
  const fmt = (iso?: string | null) =>
    iso ? dayjs(iso).format(dateFormatLabel ?? 'MM/DD/YYYY') : null;
  if (derived === 'requested' && consent?.last_request_sent_at)
    return `Sent on ${fmt(consent.last_request_sent_at)}`;
  if (derived === 'expired' && consent?.expires_at)
    return `Consent expired on ${fmt(consent.expires_at)}`;
  return null;
};

// Statuses where "Mark consent manually" reads "Extend consent manually" — an
// active consent whose validity is being pushed out rather than first recorded.
export const EXTENDABLE_STATUSES: DerivedConsentStatus[] = [
  'granted',
  'expiring_soon',
  'no_expiry',
];

export const EXTEND_MANUALLY_LABEL = 'Extend consent manually';

export const consentActionLabel = (
  action: ConsentActionKey,
  status: DerivedConsentStatus | null
): string => {
  if (action === 'request' && status && RENEWABLE_STATUSES.includes(status))
    return REQUEST_EXTENSION_LABEL;
  if (
    action === 'mark_manually' &&
    status &&
    EXTENDABLE_STATUSES.includes(status)
  )
    return EXTEND_MANUALLY_LABEL;
  return CONSENT_ACTION_CONFIG[action].label;
};

// Ordered menu actions per status (product mapping, 2026-07). `anonymize`
// entries render only when the user has the anonymize permission.
export const CONSENT_STATUS_ACTIONS: Record<
  DerivedConsentStatus,
  ConsentActionKey[]
> = {
  not_collected: ['request', 'mark_manually', 'anonymize'],
  requested: ['remind', 'mark_manually', 'withdraw', 'anonymize'],
  // `request` on a granted consent renders as "Request consent extension".
  granted: ['mark_manually', 'request', 'withdraw'],
  expiring_soon: ['mark_manually', 'request', 'withdraw'],
  // expired → "Request consent extension"; withdrawn → plain "Request consent" (see RENEWABLE_STATUSES).
  expired: ['request', 'mark_manually', 'anonymize'],
  withdrawn: ['request', 'mark_manually', 'anonymize'],
  refused: ['request', 'anonymize'],
  blocked: ['anonymize'],
  not_required: ['mark_manually'],
  // `withdraw` is offered only where the BE's withdraw! accepts it
  // (requested / granted / no_expiry) — never on an already withdrawn,
  // refused, expired or not-required consent.
  no_expiry: ['mark_manually', 'withdraw'],
};

// Single source of truth for consent error copy (BE contract §13).
const CONSENT_ERRORS = consentErrors as Record<
  string,
  { message: string; tooltip?: string }
>;

const FALLBACK_ERROR = 'Something went wrong. Try again.';

// Serialize an error from any shape we produce: axios `response.data`, the
// asserted ATS envelope (`responseData` / `message`), or a plain reject value.
const errorRaw = (error: unknown): string => {
  const e = error as {
    response?: { data?: unknown };
    responseData?: unknown;
    message?: string;
  };
  return JSON.stringify(
    e?.response?.data ?? e?.responseData ?? e?.message ?? error ?? ''
  );
};

// BE errors arrive either bare (["err_x"]) or wrapped ("Validation failed:
// Mode err_x") — substring-match, never equality-match.
export const consentErrorMessage = (
  error: unknown,
  fallback: string = FALLBACK_ERROR
): string => {
  const raw = errorRaw(error);
  const code = Object.keys(CONSENT_ERRORS).find((k) => raw.includes(k));
  return code ? CONSENT_ERRORS[code].message : fallback;
};

// Shorter copy for a disabled action's tooltip (product gives a terser form for some codes);
// falls back to the toast message when no tooltip-specific copy exists.
export const consentTooltipMessage = (
  error: unknown,
  fallback: string = FALLBACK_ERROR
): string => {
  const raw = errorRaw(error);
  const code = Object.keys(CONSENT_ERRORS).find((k) => raw.includes(k));
  if (!code) return fallback;
  return CONSENT_ERRORS[code].tooltip ?? CONSENT_ERRORS[code].message;
};

// True when the anonymize call failed because the candidate is still active —
// callers re-submit with confirm:true. The BE signals this either via the
// error code or a `has_active_applications: true` flag on the 422 body.
export const isActiveApplicationsError = (error: unknown): boolean => {
  const raw = errorRaw(error);
  return (
    raw.includes('err_confirm_required_active_applications') ||
    raw.includes('"has_active_applications":true')
  );
};
